import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Check, Phone } from 'lucide-react';
import { PRICING, ROUTES } from '@/lib/index';

const programs = [
  {
    name: 'PATROCINIOS',
    desc: 'Equipos y atletas pueden obtener uniformes a precio preferencial a cambio de promoción de la marca.',
    color: 'border-primary/40 hover:border-primary',
    badge: 'bg-primary text-primary-foreground',
  },
  {
    name: 'COMISIONES',
    desc: 'Recibe comisión por cada pedido que refiereas. ¡Gana dinero recomendando uniformes de calidad!',
    color: 'border-accent/40 hover:border-accent',
    badge: 'bg-accent text-accent-foreground',
  },
  {
    name: 'CASH BACK',
    desc: 'Acumula beneficios en cada compra. Entre más pides, más ganas de vuelta en tus próximos pedidos.',
    color: 'border-chart-5/40 hover:border-chart-5',
    badge: 'bg-chart-5 text-foreground',
  },
];

const included = [
  'Full print en toda la playera',
  'Diseño personalizado incluido',
  'Un solo diseño por pedido',
  'Envío a toda la República',
  'Asesoría de diseño sin costo',
  'Muestras digitales antes de producción',
];

export default function Pricing() {
  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 40 }}
          className="text-center mb-14"
        >
          <p className="text-primary font-bold text-xs tracking-widest uppercase mb-3">PRECIOS</p>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-foreground mb-4">
            ENTRE MÁS PIDES,<br />
            <span className="text-primary">MÁS AHORRAS</span>
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Precios competitivos sin sacrificar calidad premium. Un solo pedido, un solo diseño.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
          {PRICING.map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, type: 'spring', stiffness: 250, damping: 35 }}
              className={`relative rounded-xl border p-6 text-center transition-all duration-300 ${
                p.highlight
                  ? 'bg-primary border-primary shadow-2xl shadow-primary/20 scale-105'
                  : 'bg-card border-border hover:border-primary/50'
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary-foreground text-primary text-[9px] font-black tracking-widest rounded-full whitespace-nowrap">
                  MÁS POPULAR
                </span>
              )}
              <p className={`text-[10px] font-black tracking-widest uppercase mb-3 ${p.highlight ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                {p.label}
              </p>
              <p className={`text-3xl font-black font-mono mb-1 ${p.highlight ? 'text-primary-foreground' : 'text-primary'}`}>
                {p.price}
              </p>
              {p.price !== 'CONSULTA' && (
                <p className={`text-[10px] mb-2 ${p.highlight ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                  por pieza
                </p>
              )}
              <p className={`text-[11px] font-bold ${p.highlight ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                {p.quantity}
              </p>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground mb-12 font-mono">
          * Precios aplicables a un solo pedido con un solo diseño. Incluye full print + diseño.
        </p>

        {/* What's Included */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-card border border-border rounded-xl p-8 mb-12"
        >
          <h2 className="text-xl font-black uppercase tracking-widest text-primary mb-6 text-center">
            ¿QUÉ INCLUYE TU PEDIDO?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {included.map((item) => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Check size={12} className="text-primary" />
                </div>
                <span className="text-sm text-foreground font-medium">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Programs */}
        <div className="mb-12">
          <h2 className="text-2xl font-black uppercase tracking-widest text-foreground text-center mb-8">
            NUESTROS <span className="text-primary">PROGRAMAS</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {programs.map((prog, i) => (
              <motion.div
                key={prog.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, type: 'spring', stiffness: 250, damping: 35 }}
                className={`bg-card border rounded-xl p-6 transition-all duration-300 ${prog.color}`}
              >
                <span className={`inline-block px-3 py-1 text-[10px] font-black tracking-widest rounded mb-4 ${prog.badge}`}>
                  {prog.name}
                </span>
                <p className="text-sm text-muted-foreground leading-relaxed">{prog.desc}</p>
                <a
                  href="https://wa.me/528333007124?text=Hola,%20me%20interesa%20el%20programa%20de%20"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-4 text-xs text-primary font-bold hover:underline"
                >
                  Más información <ChevronRight size={12} />
                </a>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 200, damping: 40 }}
          className="text-center bg-card border border-primary/30 rounded-xl p-10"
        >
          <h3 className="text-2xl font-black uppercase tracking-widest text-foreground mb-3">
            ¿NECESITAS UNA COTIZACIÓN?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Para precios de 400+ piezas o proyectos especiales, contáctanos directamente.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to={ROUTES.ORDER}
              className="px-8 py-3 bg-primary text-primary-foreground font-black text-xs tracking-widest uppercase rounded hover:bg-primary/90 transition-all flex items-center gap-2"
            >
              HACER MI PEDIDO <ChevronRight size={14} />
            </Link>
            <a
              href="https://wa.me/528333007124?text=Hola,%20necesito%20cotización%20para%20mis%20uniformes"
              target="_blank"
              rel="noreferrer"
              className="px-8 py-3 border border-primary text-primary font-bold text-xs tracking-widest uppercase rounded hover:bg-primary/10 transition-all flex items-center gap-2"
            >
              <Phone size={14} /> WHATSAPP
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
